import { createAcl, defineAclRules } from "vue-simple-acl"
import { computed } from "vue"

import { ConvertRoleWithAccess, Permission } from "@/models/User"
import { Getters, store } from "@/store"
import { hideElementByAccount } from "@/utils"

const user = computed(() => store.getters[Getters.GET_USER_CURRENT_PROFILE])
const currentTeam = computed(() => store.getters[Getters.GET_SESSION_TEAM])
const accountId = computed(() => store.getters[Getters.GET_ACCOUNT_ID])
const account = computed(() => store.getters[Getters.GET_ACCOUNT])
const sessionAccount = computed(() => store.getters[Getters.GET_SESSION_ACCOUNT])
const currentProject = computed(() => store.getters[Getters.GET_PROJECT])

const rules = () =>
  defineAclRules(setRule => {
    setRule("admin", user => user?.isAdmin(accountId.value))
    setRule("superAdmin", user => user?.superAdmin)
    setRule("is-email-verified", user => user?.emailVerified)
    setRule("canAddInlineItem", user => {
      if (!currentTeam.value || !user || !user.itemCreationAccessByTeamId) return false

      const rolesOfAccess = currentTeam.value.rolesOfAccessToItemCreation as string[]

      return rolesOfAccess.includes(user.itemCreationAccessByTeamId[currentTeam.value.id])
    })

    setRule("canReviewTimesheet", user => {
      if (user?.superAdmin) return true
      const permission = user.permissions as Permission
      if (!permission) return false
      return !!permission[accountId.value]?.timesheetApprovalEnabled
    })

    setRule("teamPlan", user => {
      return true
    })

    setRule("hasSubscription", user => {
      if (currentProject.value.demo || !account.value) return true
      return account.value.hasSubscriptionAccess
    })

    setRule("enterprisePlan", user => {
      if (!account.value) return false
      return (
        account.value.enterprisePermission &&
        Object.values(account.value.enterprisePermission).some(permission => permission)
      )
    })

    setRule("hasSubscriptionIgnoreDemo", user => {
      if (!account.value) return false
      return account.value.hasSubscriptionAccess
    })

    setRule("accessToTimesheet", user => {
      if (currentProject.value?.demo || !account.value) return true
      return account.value.hasSubscriptionAccess && account.value.enterprisePermission.timesheet
    })

    setRule("accessToAi", user => {
      if (!account.value) return false
      return (
        (account.value.hasSubscriptionAccess && account.value.enterprisePermission.ai_enabled) ||
        (account.value.paymentSubscription?.type?.includes("BUSINESS") &&
          account.value.paymentSubscription?.status === "TRIALING")
      )
    })

    setRule("isTrial", user => {
      if (!account.value) return false
      return account.value.paymentSubscription?.status === "TRIALING"
    })

    setRule("isEnterpriseTrial", user => {
      if (!account.value) return false
      return (
        account.value.paymentSubscription?.type?.includes("BUSINESS") &&
        account.value.paymentSubscription?.status === "TRIALING"
      )
    })

    setRule("isBusiness", user => {
      if (!account.value) return false
      return account.value.paymentSubscription?.type?.includes("BUSINESS")
    })

    setRule("showElementByAccount", () => {
      return !hideElementByAccount(user.value)
    })

    setRule("isProjectSimpleAccount", () => {
      return sessionAccount.value?.urlName === "project-simple"
    })
  })

const simpleAcl = createAcl({
  user,
  rules
})

export default simpleAcl
